import { alpha, Box, Container, Grid, MobileStepper, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { Button, IconButton } from 'design-system';
import { ButtonView as ButtonType } from 'interfaces/graphqlViews';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdLocalShipping } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router';
import { getPrefixedRoute } from 'services/theming';
import { OperationResult } from 'urql';
import ActionButton from 'components/ActionButton';
import Form from 'components/Form';
import NetworkLoading from 'components/NetworkLoading';
import QueryResult from 'components/QueryResult';
import ShipmentGraphic from 'components/ShipmentGraphic';
import TableDialog, { TableDialogVariant } from 'components/TableDialog';
import { HERO_COVERS, HeroPage } from 'config/hero-covers';
import useIsViewport from 'hooks/useIsViewport';
import useOfflineMutation from 'hooks/useOfflineMutation';
import useOfflineQuery from 'hooks/useOfflineQuery';
import useReturnPath from 'hooks/useReturnPath';
import { useShipmentsOrSalesPath } from 'hooks/useShipmentsOrSalesPath';
import { SnackbarVariant, useSnackbar } from 'hooks/useSnackBar';
import { buildShipmentItemInputsForRows } from 'utils/buildShipmentItemInputs';
import { buildUpdatedForm, getFormValues, parseForm } from 'utils/form';
import { updateExpandableTableFields } from 'utils/table';
import {
  ActionIntent,
  EditShipmentInput,
  EditShipmentIntent,
  EditShipmentMutationDocument,
  EditShipmentMutationMutation,
  EditShipmentMutationMutationVariables,
  ExpandableTable as ExpandableTableType,
  Field,
  FormFragment as FormData,
  ShipmentStatus,
  TableDialogFragment,
  useEditShipmentsQuery,
} from 'generated/graphql';

const Hero = lazy(() => import('components/Hero'));
const ExpandableTable = lazy(() => import('components/ExpandableTable/ExpandableTable'));

// One step of the bulk shipment edit page: a single shipment's own payload for
// the page's intent (the same shape the single edit screen renders) — its
// products table, fields form, action button and dedup tags — plus whether the
// intent has already been applied to it.
type Section = {
  id: string;
  title: string;
  form: FormData;
  itemsTable?: ExpandableTableType;
  actionButton?: ButtonType;
  eTag: string[];
  complete: boolean;
};

// Compact step label from a shipment's title. Reused single-shipment titles look
// like "Confirm dispatch: #123"; surface the "#123", falling back to the title.
const stepLabel = (title: string): string => title.match(/#\d+/)?.[0] ?? title;

const EditShipments = () => {
  const { truckId, intent } = useParams<{ truckId: string; intent?: EditShipmentIntent }>();
  const editIntent = intent ?? EditShipmentIntent.DispatchSeller;
  const navigate = useNavigate();
  const isMobile = useIsViewport();
  const { showSnackbar } = useSnackbar();
  const shipmentsOrSalesPath = useShipmentsOrSalesPath();
  const returnPath = useReturnPath() ?? shipmentsOrSalesPath;

  const [sections, setSections] = useState<Section[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Server-driven follow-up dialog (e.g. discard/keep an expected short supply)
  // shown when a submit returns hasDialog — same as the single flow. The edit
  // only commits once the operator resolves it, so it's not treated as done here.
  const [postSubmitDialogs, setPostSubmitDialogs] = useState<TableDialogFragment[]>();
  const initialisedRef = useRef<unknown>(undefined);

  const [queryResult, refreshShipments] = useOfflineQuery(
    `edit-shipments-${editIntent}-${truckId}`,
    useEditShipmentsQuery,
    {
      variables: { truckID: truckId ?? '', intent: editIntent },
      pause: !truckId,
      requestPolicy: 'cache-and-network',
    },
  );

  const [, editShipmentMutation] = useOfflineMutation<
    EditShipmentMutationMutation,
    EditShipmentMutationMutationVariables
  >(
    `bulk-edit-shipment-${editIntent}-${truckId}`,
    EditShipmentMutationDocument,
    editIntent === EditShipmentIntent.DispatchSeller,
  );

  const data = queryResult.data?.editShipments;

  // Seed local editable state once per fetched payload. Each shipment is a full
  // per-shipment payload for the page's intent, so it carries its own products table.
  useEffect(() => {
    if (!data || initialisedRef.current === data) return;
    initialisedRef.current = data;
    setSections(
      data.shipments.map((s) => ({
        id: s.id,
        title: s.title,
        form: parseForm(s.form as FormData),
        itemsTable: s.itemsTable as ExpandableTableType,
        actionButton: s.actionButton as ButtonType,
        eTag: s.eTag,
        complete: s.complete,
      })),
    );
    setCompletedIds(new Set());
    // Start on the first shipment the intent hasn't been applied to yet.
    const firstOutstanding = data.shipments.findIndex((s) => !s.complete);
    setActiveStep(firstOutstanding >= 0 ? firstOutstanding : 0);
  }, [data]);

  // Keep the active step within bounds if the section list shrinks.
  useEffect(() => {
    setActiveStep((step) => Math.min(step, Math.max(0, sections.length - 1)));
  }, [sections.length]);

  const activeSection = sections[activeStep];
  const isLastStep = activeStep === sections.length - 1;

  const updateSectionForm = (shipmentID: string, field: Field, value: string, values?: string[]) =>
    setSections((prev) =>
      prev.map((s) => (s.id === shipmentID ? { ...s, form: buildUpdatedForm(s.form, field, value, values) } : s)),
    );

  // Edits write through to the active shipment's own products table.
  const updateItemsTable = (rowId: string, cellIndex: number, newValue: string) =>
    setSections((prev) =>
      prev.map((s) =>
        s.id === activeSection?.id && s.itemsTable
          ? { ...s, itemsTable: updateExpandableTableFields(s.itemsTable, cellIndex, rowId, newValue) }
          : s,
      ),
    );

  // A step is done if it was already applied on load or during this session.
  const isComplete = (section?: Section) => !!section && (section.complete || completedIds.has(section.id));
  const currentComplete = isComplete(activeSection);

  const activeTable = useMemo<ExpandableTableType | undefined>(() => activeSection?.itemsTable, [activeSection]);

  // Mark a just-actioned shipment done, then jump to the next one still
  // outstanding — or leave once every shipment on the truck is done.
  const advancePastComplete = (completedId: string, redirectUrl?: string) => {
    const completed = new Set(completedIds).add(completedId);
    setCompletedIds(completed);
    const next = sections.findIndex((s) => !s.complete && !completed.has(s.id));
    if (next === -1) {
      navigate(getPrefixedRoute(redirectUrl || returnPath), { replace: true });
    } else {
      setActiveStep(next);
    }
  };

  // Apply the intent to the current shipment on its own (one per step), then
  // advance to the next — or leave once the last one is done. Each shipment is a
  // normal per-shipment edit, so there's no all-at-once bulk submit.
  const handleSubmit = async () => {
    if (!activeSection || currentComplete) return;

    const rows = activeSection.itemsTable?.groups.flatMap((g) => g.rows) ?? [];
    const input: EditShipmentInput = {
      id: activeSection.id,
      status: ShipmentStatus.Unknown,
      intent: editIntent,
      actionIntent: ActionIntent.Request,
      eTag: activeSection.eTag,
      fields: buildShipmentItemInputsForRows(activeSection.itemsTable?.columns ?? [], rows, {
        formFields: await getFormValues(activeSection.form),
      }),
    };

    setIsSubmitting(true);
    let res;
    try {
      res = await editShipmentMutation(
        { input },
        { success: 'Shipment updated.', error: 'Failed to update shipment.' },
        { additionalTypenames: ['TablesScreen', 'TableGroup', 'ExpandableTableRow'] },
      );
    } finally {
      setIsSubmitting(false);
    }
    if (res.error) return;

    const response = res.data?.editShipment;
    if (response?.hasToast) {
      showSnackbar({ message: response.toast.value, color: response.toast.color.toLowerCase() }, SnackbarVariant.ALERT);
    }

    // The backend may ask what to do with an expected short supply (discard it or
    // create a follow-up shipment) — the same dialog the single edit flow shows.
    // The edit only commits once the operator resolves it, so don't advance or
    // mark this shipment done here; the dialog's onClose refetches the real state.
    if (response?.hasDialog) {
      if (!response?.success || isLastStep) {
        setPostSubmitDialogs(response.dialogs);
      }
      return;
    }

    advancePastComplete(activeSection.id, response?.redirectUrl);
  };

  // The per-shipment stepper lives in the header (attached to the hero). Steps are
  // clickable so the operator can jump around a large truck; it fills the header
  // for a few shipments and scrolls horizontally only when steps get cramped.
  const stepper = sections.length > 0 && (
    <Box sx={{ width: '100%' }}>
      <Stepper
        nonLinear
        activeStep={activeStep}
        alternativeLabel
        sx={(theme) => ({
          minWidth: sections.length * 130,
          '& .MuiStepLabel-label': {
            color: theme.palette.common.white,
            opacity: 0.7,
            // Tighten the default 16px gap between the icon and its label.
            '&.MuiStepLabel-alternativeLabel': { marginTop: theme.spacing(0.5) },
            '&.Mui-active': {
              color: theme.palette.common.white,
              opacity: 1.0,
              fontWeight: theme.typography.fontWeightBold,
            },
            '&.Mui-completed': { color: theme.palette.common.white, opacity: 0.7 },
          },
          '& .MuiStepIcon-root': {
            color: alpha(theme.palette.common.white, 0.3),
            // Active step highlighted in the brand accent (info), which stands out
            // against the dark hero. primary is the hero's own colour, so it would
            // blend; info.main is the same orange used for the map pin and badges.
            '&.Mui-active': {
              color: theme.palette.info.main,
            },
            // Completed steps show a green check.
            '&.Mui-completed': { color: theme.palette.success.main },
          },
        })}
      >
        {sections.map((section, index) => (
          <Step key={section.id} completed={isComplete(section)}>
            <StepLabel onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer' }}>
              {stepLabel(section.title)}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );

  // On small screens the labelled stepper is too cramped, so use a compact
  // MobileStepper: the active shipment's title with sequential back/next controls
  // and a "n / total" position readout.
  const mobileStepper = sections.length > 0 && (
    <Stack sx={{ width: '100%', alignItems: 'center' }}>
      <Typography sx={(theme) => ({ color: theme.palette.common.white, fontWeight: theme.typography.fontWeightBold })}>
        {activeSection && stepLabel(activeSection.title)}
      </Typography>
      <MobileStepper
        variant="text"
        position="static"
        steps={sections.length}
        activeStep={activeStep}
        sx={(theme) => ({ width: '100%', background: 'transparent', color: theme.palette.common.white, padding: 0 })}
        backButton={
          <Button
            size="small"
            color="inherit"
            onClick={() => setActiveStep((step) => step - 1)}
            disabled={activeStep === 0 || isSubmitting}
            sx={(theme) => ({ color: theme.palette.common.white })}
          >
            <MdChevronLeft />
            Back
          </Button>
        }
        nextButton={
          <Button
            size="small"
            color="inherit"
            onClick={() => setActiveStep((step) => step + 1)}
            disabled={isLastStep || isSubmitting}
            sx={(theme) => ({ color: theme.palette.common.white })}
          >
            Next
            <MdChevronRight />
          </Button>
        }
      />
    </Stack>
  );

  return (
    <QueryResult result={queryResult} empty="Could not load shipments" isProgressiveLoadingEnabled>
      <Container>
        <Hero
          // Title/subtitle come split from the backend ("Dispatch shipments" or
          // "Load shipments" / "on truck #T123"); the subtitle renders on desktop
          // only, so on mobile just the short title shows and avoids wrapping.
          title={data?.title ?? 'Edit shipments'}
          subtitle={data?.subtitle}
          cover={HERO_COVERS[HeroPage.EditShipments]}
          isReturn
          returnPath={returnPath}
          actions={stepper}
          childrenMobile={mobileStepper}
        />
        {queryResult.fetching && sections.length === 0 && <NetworkLoading hideHero />}

        {/* Nothing on the truck needs this intent: the payload carries no shipments. */}
        {data && sections.length === 0 ? (
          <Stack
            direction="column"
            spacing={1}
            sx={(theme) => ({
              width: '100%',
              padding: theme.spacing(4),
              borderRadius: theme.spacing(1),
              border: `1px dashed ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.grey[50],
              color: theme.palette.text.secondary,
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            })}
          >
            <MdLocalShipping size={56} />
            <Typography variant="body2">Nothing left to do on this truck.</Typography>
          </Stack>
        ) : (
          activeSection && (
            <Stack
              spacing={2}
              sx={(theme) => ({ paddingY: theme.spacing(1), paddingBottom: isMobile ? theme.spacing(14) : undefined })}
            >
              <Grid container sx={(theme) => ({ gap: theme.spacing(1), justifyContent: 'space-between' })}>
                {/* Left: the active shipment's products. */}
                <Grid size={{ xs: 12, md: 7.8 }}>
                  {activeTable && (
                    <Suspense fallback={null}>
                      <ExpandableTable table={activeTable} onFieldUpdate={updateItemsTable} />
                    </Suspense>
                  )}
                </Grid>

                {/* Right: the active shipment's own delivery details / references. */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack
                    sx={(theme) => ({
                      background: theme.palette.common.white,
                      border: `1px solid ${theme.palette.grey[300]}`,
                      borderRadius: theme.spacing(1),
                      padding: theme.spacing(2),
                      [theme.breakpoints.down('md')]: {
                        background: theme.palette.background.default,
                        border: 'none',
                        padding: 0,
                      },
                    })}
                  >
                    <Form
                      hasWhiteFields
                      hideTitle
                      form={activeSection.form}
                      onFieldUpdateNew={async (_g, f, v, vs) => updateSectionForm(activeSection.id, f, v, vs)}
                    />
                    {/* On mobile the right panel stacks below the long table, so pin
                        the action to a fixed bottom bar (above the nav) — the header's
                        mobile stepper already handles prev/next, so drop the chevrons
                        there. On desktop it stays inline, chevrons flanking the button. */}
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={
                        isMobile
                          ? (theme) => ({
                              position: 'fixed',
                              bottom: 50,
                              left: 0,
                              width: '100vw',
                              alignItems: 'center',
                              background: theme.palette.background.paper,
                              padding: theme.spacing(1, 1.5, 1.5),
                              boxShadow: '1px 1px 5px rgba(0,0,0,0.4)',
                              zIndex: 5,
                            })
                          : (theme) => ({ marginTop: theme.spacing(1) })
                      }
                    >
                      {activeStep > 0 && !isMobile && (
                        <IconButton
                          color="secondary"
                          aria-label="Previous shipment"
                          onClick={() => setActiveStep((step) => step - 1)}
                          disabled={isSubmitting}
                          sx={(theme) => ({ border: `1px solid ${theme.palette.secondary.main}` })}
                        >
                          <MdChevronLeft />
                        </IconButton>
                      )}
                      {/* Same server-driven action button the single edit page uses
                          (colour/variant/label come from the server). A shipment the
                          intent has already been applied to can't be actioned again —
                          it's disabled and reads the server's past-tense label
                          ("Dispatched", "Loaded"); the operator moves on with the
                          chevrons. */}
                      {activeSection.actionButton && (
                        <Box sx={{ flex: 1 }}>
                          <ActionButton
                            key={activeSection.id}
                            // Cast around the depth-limited recursive fragment type
                            // (Action.group) that isn't structurally the full Button.
                            {...(activeSection.actionButton as ButtonType)}
                            eTag={activeSection.eTag}
                            loading={isSubmitting}
                            disabled={activeSection.actionButton.disabled || isSubmitting || currentComplete}
                            onClick={handleSubmit}
                            fullWidth
                            size="medium"
                          >
                            {currentComplete ? data?.completeLabel : activeSection.actionButton.value}
                          </ActionButton>
                        </Box>
                      )}
                      {!isLastStep && !isMobile && (
                        <IconButton
                          color="secondary"
                          aria-label="Next shipment"
                          onClick={() => setActiveStep((step) => step + 1)}
                          disabled={isSubmitting}
                          sx={(theme) => ({ border: `1px solid ${theme.palette.secondary.main}` })}
                        >
                          <MdChevronRight />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          )
        )}
      </Container>
      <TableDialog
        data={postSubmitDialogs}
        variant={TableDialogVariant.Detailed}
        graphic={<ShipmentGraphic />}
        onClose={() => {
          setPostSubmitDialogs(undefined);
          advancePastComplete(activeSection.id);
          // Whether the short was discarded, kept as a new shipment, or the dialog
          // was dismissed, the authoritative state (and any new shipment) now lives
          // on the server — refetch and re-seed rather than guess.
          refreshShipments({ requestPolicy: 'network-only' });
        }}
      />
    </QueryResult>
  );
};

export default EditShipments;
